/**
 * Swipe-to-mark-read wrapper for a notification card on the Admin
 * Notification Center page.
 *
 * Mobile: drag RIGHT → reveal emerald/teal "Mark as read" affordance →
 *   release past threshold (or fast flick) → onMarkRead(id). Matches
 *   PAGES.md §3.72 ("Swipe-right to mark read").
 * Desktop (lg+): a tiny visible "Mark as read" button on the right of the
 *   card body provides the keyboard / mouse path (WCAG 2.5.1 requires that
 *   any path-based gesture have an alternative single-pointer activation).
 *
 * Tap/click on the body itself opens the notification via the parent's
 * `onClick` — the swipe wrapper only intercepts real drags.
 */

import { useEffect, useRef } from 'react';
import { useSwipeToAction, SwipeRevealBackground } from '@bbpos/ui';
import type { Notification } from '@/services/notification.service';
import { NotificationCardBody } from './NotificationCardBody';
import { cn } from '@/constants/design-tokens';

// ─── Icons ──────────────────────────────────────────────────────────────────

function CheckIcon({ className = 'h-7 w-7' }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CheckSmallIcon() {
  return (
    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

// ─── Keyboard fallback button ───────────────────────────────────────────────

interface MarkReadButtonProps {
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

function MarkReadButton({ onClick }: MarkReadButtonProps) {
  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    // Stop the parent <button> (NotificationCardBody) from also firing.
    e.stopPropagation();
    onClick(e);
  };
  const handlePointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    // Don't let the swipe wrapper hijack the pointer for this nested control.
    e.stopPropagation();
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      onPointerDown={handlePointerDown}
      aria-label="Mark as read"
      title="Mark as read"
      className={cn(
        'hidden lg:inline-flex shrink-0 items-center justify-center w-11 h-11 ml-1 rounded-xl',
        'text-neutral-400 hover:text-emerald-600 dark:hover:text-emerald-300',
        'hover:bg-emerald-50 dark:hover:bg-emerald-900/30',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500',
        'transition-colors duration-150',
      )}
    >
      <CheckSmallIcon />
    </button>
  );
}

// ─── Component ──────────────────────────────────────────────────────────────

export interface SwipeableNotificationCardProps {
  notification: Notification;
  onClick: () => void;
  onMarkRead: (id: string) => void;
}

export function SwipeableNotificationCard({
  notification,
  onClick,
  onMarkRead,
}: SwipeableNotificationCardProps) {
  const isUnread = !notification.is_read;
  const vibratedRef = useRef(false);

  const { bind, state, progress, totalDelta } = useSwipeToAction({
    onCommit: () => {
      onMarkRead(notification.id);
    },
    direction: 'right',
    disabled: !isUnread,
  });

  // Vibrate exactly once per gesture, on the first crossing of progress=1.
  useEffect(() => {
    if (state !== 'dragging') {
      vibratedRef.current = false;
      return;
    }
    if (progress >= 1 && !vibratedRef.current) {
      vibratedRef.current = true;
      try {
        navigator.vibrate?.(15);
      } catch {
        /* vibration unavailable — silently ignore */
      }
    }
  }, [progress, state]);

  const isMidGesture = state === 'dragging' || state === 'settling' || state === 'committed';
  const handleClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    // The native click runs after pointerup. If the user actually dragged
    // more than the tap tolerance OR a commit/settle is in progress, swallow
    // the click so the parent onClick doesn't open the notification.
    if (totalDelta > 8 || isMidGesture) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const handleMarkReadButton = () => {
    onMarkRead(notification.id);
  };

  // Read cards: render the body as-is with no wrapper or reveal layer.
  if (!isUnread) {
    return <NotificationCardBody notification={notification} onClick={onClick} />;
  }

  return (
    <div
      ref={bind.ref as React.RefCallback<HTMLDivElement>}
      onPointerDown={bind.onPointerDown}
      onPointerMove={bind.onPointerMove}
      onPointerUp={bind.onPointerUp}
      onPointerCancel={bind.onPointerCancel}
      onClickCapture={handleClickCapture}
      className="relative rounded-2xl"
      style={{ touchAction: bind.style.touchAction }}
      aria-roledescription="Swipeable notification, swipe right to mark as read"
      data-swipe-state={state}
      data-testid="swipeable-notification-card"
    >
      <SwipeRevealBackground
        progress={progress}
        state={state}
        label="Release to mark read"
        icon={<CheckIcon className="h-7 w-7" />}
        side="left"
      />

      <div
        style={bind.style}
        className="relative z-10"
      >
        <NotificationCardBody
          notification={notification}
          onClick={onClick}
          trailingAccessory={<MarkReadButton onClick={handleMarkReadButton} />}
        />
      </div>
    </div>
  );
}
